import lodashKeyBy from 'lodash/fp/keyBy';
import lodashDefaultsDeep from 'lodash/fp/defaultsDeep';

import typeCheck from 'common/util/typeCheck';

import { hideFieldIf } from 'client/helpers/formikHelpers';

import {
  LISTING_STATUS_LIST,
  LISTING_MLS_STATUS_LIST,
  LISTING_APPLICATION_POLICY_FIRST,
  LISTING_APPLICATION_POLICY_BEST,
  LISTING_OCCUPANT_TYPES,
} from 'common/listingConstants';

import {
  AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_OPTION_ITEMS,
  PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS,
  PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS,
} from 'common/marketingPartnerListingConstants';

import { PROPERTY_TYPE_OPTIONS_VALUES } from 'common/propertyTypes';

import {
  LEASE_TEMPLATE_TYPE_STANDARD,
  LEASE_TEMPLATE_TYPE_CUSTOM_LEASE,
} from 'common/financeConstants';

import rentPaymentOptionItems from 'client/helpers/rentPaymentOptionItems';
import propertyManagerOptionItems from 'client/helpers/propertyManagerOptionItems';
import petWeightOptionItems from 'client/helpers/petWeightOptionItems';
import postToMlsOptionItems from 'client/helpers/postToMlsOptionItems';

import { SR_CHECKBOX_LIST_THEME_ICON_BOX, SR_CHECKBOX_THEME_BOX } from 'client/components/Checkbox';

export const LISTING_FORM_TYPE_APARTMENT_PROPERTY_ATTRIBUTES =
  'LISTING_FORM_TYPE_APARTMENT_PROPERTY_ATTRIBUTES'; // eslint-disable-line max-len
export const LISTING_FORM_TYPE_FLOOR_PLAN_PROPERTY_ATTRIBUTES =
  'LISTING_FORM_TYPE_FLOOR_PLAN_PROPERTY_ATTRIBUTES'; // eslint-disable-line max-len

export const apartmentPropertyAttributesFieldgroup = [
  'hasPool',
  'hasDryer',
  'dryerMaintainedByLandlord',
  'hasRefrigerator',
  'refrigeratorMaintainedByLandlord',
  'hasWasher',
  'washerMaintainedByLandlord',
  'hasWasherDryerConnections',
  'hasBambooFlooring',
  'hasBrick',
  'hasCarpetFlooring',
  'hasConcreteFlooring',
  'hasCorkFlooring',
  'hasLaminateFlooring',
  'hasLinoleumFlooring',
  'hasMarbleFlooring',
  'hasNoCarpetFlooring',
  'hasParquetFlooring',
  'hasSlateFlooring',
  'hasStoneFlooring',
  'hasTerrazzoFlooring',
  'hasTileFlooring',
  'hasVinylTileFlooring',
  'hasVinylFlooring',
  'hasVinylSheetFlooring',
  'hasWoodFlooring',
  'hasDishWasher',
  'hasGarbageDisposal',
  'hasGasStove',
  'hasElectricStove',
  'hasMicrowave',
  'petWeight',
  'dogsAllowed',
  'catsAllowed',
  'petsAllowed',
  'maximumPetsAllowed',
  'stories',
  'fenceDescription',
  'garageSpaces',
  'parkingSpaces',
  'yearBuilt',
  'yearRenovated',
  'renovatedDate',
  'hasGarage',
  'hasFence',
  'apartmentUnitsCount',
  'hasCentralHeating',
  'hasNaturalGasHeating',
  'hasRadiatorHeating',
  'policyElectricPaid',
  'policyGasPaid',
  'policyTrashPaid',
  'policyWaterPaid',
  'policyGardeningPaid',
  'policyPestControlpaid',
  'hasCentralAc',
  'applicationFee',
  'petDeposit',
  'petDepositPerPet',
  'securityDeposit',
  'petFee',
  'additionalPetFee',
  'applicationPolicy',
  'administrationFee',
  'minLeaseMonths',
  'acceptsGuarantor',
  'monthlyPetRent',
  'maxLeaseMonths',
  'leaseLengthNegotiable',
  'section8',
  'gateCode',
  'electronicLockCode',
  'electronicLockInstructions',
  'hasBasement',
  'hasPatio',
  'hasBalcony',
  'hasDeck',
  'isFurnished',
  'isNotFurnished',
  'smokeFree',
  'monthToMonthLeaseTerm',
  'rentToOwnLeaseTerm',
  'highSpeedInternetIncluded',
  'highSpeedInternetAvailable',
  'hasRampedEntry',
  'hasAccessibleParking',
  'hasLevelStyleDoorHandles',
  'environmentalControlsAboveFloor',
  'grabBarsInBathroom',
  'lowTresholdShower',
  'hasTTurnInBathroom',
  'accessibleBathroomVanities',
  'hasFrontLoadingKitchenAppliances',
  'hasAccessibleKitchenCounters',
  'hasClearFloorAreaFrontOfSink',
  'has32WideDoorways',
  'has36WideDoorways',
  'hasAirConditioning',
  'hasOven',
  'largeDogsAllowed',
  'electricityIncluded',
  'gasIncluded',
  'heatIncluded',
  'waterIncluded',
  'trashPickUpIncluded',
  'hasWaterSoftener',
  'description',
  'hasCommunalWasherDryer',
  'hasDogWalkPark',
  'kitchenCountertopMaterial',
  'hasBusinessCenter',
  'hasConferenceRoom',
  'hasJoggingTrail',
  'hasRoofDeck',
  'elevatorInBuilding',
  'hasStackedWasherDryer',
  'hasOnlinePayments',
  'floorLevel',
  'hasWheelchairAccessible',
  'coveredParkingCount',
  'surfaceParkingCount',
  'garageAttachedParkingSpots',
  'garageDetachedParkingSpots',
  'hasOnSiteManagement',
  'hasFencedYards',
  'hasYard',
  'hasControlledBuildingAccess',
  'hasCeilingFans',
  'hasFireplace',
  'hasExtraStorage',
  'hasWalkInClosets',
  'kitchenAppliancesColor',
  'hasShowerTub',
  'numberOfFloors',
  'valetTrashFee',
  'pestControlFee',
  'wasteWaterFee',
  'monthlyCommunityFee',
  'monthlyCommunityFeeDescription',
  'carportFee',
  'reservedParkingSpotFee',
  'bikeRackFee',
  'electricFee',
  'gasFee',
  'waterAndSewerFee',
  'cableAndInternetCombinedFee',
  'cableFee',
  'internetFee',
  'packageLockerSystemFee',
  'coveredParkingFee',
  'storageFee',
  'surfaceParkingFee',
  'garageAttachedParkingSpotFee',
  'garageDetachedParkingSpotFee',
  'externalApplicationLink',
  'apartmentEmail',
  'apartmentPhone',
];

export const floorPlanPropertyAttributesFieldgroup = [
  'price',
  'squareFeet',
  'bedrooms',
  'bathrooms',
  ...apartmentPropertyAttributesFieldgroup,
];

export const listingFormTypeToFieldgroupMapping = {
  [LISTING_FORM_TYPE_APARTMENT_PROPERTY_ATTRIBUTES]: apartmentPropertyAttributesFieldgroup,
  [LISTING_FORM_TYPE_FLOOR_PLAN_PROPERTY_ATTRIBUTES]: floorPlanPropertyAttributesFieldgroup,
};

export const listingFormFields = [
  {
    id: 'mlsNumber',
    title: 'MLS number',
    type: 'number',
  },
  {
    id: 'price',
    title: 'Rent Price',
    type: 'currency',
  },
  {
    id: 'addressStreetName',
    title: 'Street Name',
    type: 'short_text',
  },
  {
    id: 'addressStreetSuffix',
    title: 'Street Suffix',
    type: 'autocomplete',
    properties: {
      placeholder: 'Rd',
      minCharMatchCount: 1,
    },
  },
  {
    id: 'addressStreetNoSuffix',
    title: 'Has no suffix',
    type: 'checkbox',
  },
  {
    id: 'addressStreetNumber',
    title: 'Street Number',
    type: 'short_text',
  },
  {
    id: 'addressZip',
    title: 'Zip Code',
    type: 'short_text',
  },
  {
    id: 'addressCity',
    title: 'City',
    type: 'short_text',
  },
  {
    id: 'addressStateCode',
    title: 'State',
    type: 'select',
  },
  {
    id: 'addressCounty',
    title: 'County',
    type: 'short_text',
  },
  {
    id: 'addressCountryCode',
    validations: ['required'],
    title: 'Country',
    type: 'select',
  },
  {
    id: 'locationLatitude',
    title: 'Location Latitude',
    type: 'number',
  },
  {
    id: 'locationLongitude',
    title: 'Location Longitude',
    type: 'number',
  },
  {
    id: 'status',
    title: 'Status',
    type: 'select',
    properties: {
      choices: LISTING_STATUS_LIST.map((option) => ({
        label: option,
        value: option,
      })),
    },
  },
  {
    id: 'mlsStatus',
    title: 'MLS Status',
    type: 'select',
    properties: {
      choices: LISTING_MLS_STATUS_LIST.map((option) => ({
        label: option,
        value: option,
      })),
    },
  },
  {
    id: 'cantTourFlag',
    title: "Can't Tour?",
    type: 'checkbox',
  },
  {
    id: 'propertyType',
    title: 'Property Type',
    type: 'select',
    properties: {
      choices: PROPERTY_TYPE_OPTIONS_VALUES.map((option) => ({
        label: option,
        value: option,
      })),
    },
  },
  {
    id: 'apartmentName',
    title: 'Complex Name',
    type: 'select',
  },
  {
    id: 'apartmentBuildingId',
    title: 'Complex Building',
    type: 'select',
  },
  {
    id: 'floorPlanId',
    title: 'Floor Plan',
    type: 'select',
    properties: {
      enableResetOption: true,
    },
  },
  {
    id: 'apartmentUnit',
    title: 'Unit #',
    type: 'short_text',
  },
  {
    id: 'apartmentUnitsCount',
    title: 'Units in Complex',
    type: 'number',
  },
  {
    id: 'floorLevel',
    title: 'Floor Level',
    type: 'number',
  },
  {
    id: 'numberOfFloors',
    title: 'Number Of Floors',
    type: 'number',
  },
  {
    id: 'securityDeposit',
    title: 'Security Deposit',
    type: 'currency',
  },
  {
    id: 'squareFeet',
    title: 'Square Feet',
    type: 'numberWithComma',
  },
  {
    id: 'bedrooms',
    title: 'Beds',
    type: 'number',
  },
  {
    id: 'bathrooms',
    title: 'Baths',
    type: 'number',
    properties: {
      step: 0.1,
    },
  },
  {
    id: 'hasPool',
    title: 'Pool',
    type: 'checkbox',
  },
  {
    id: 'hasWasherDryer',
    title: 'Washer Dryer',
    type: 'checkbox',
  },
  {
    id: 'dryerMaintainedByLandlord',
    title: 'Dryer Maintained',
    type: 'checkbox',
  },
  {
    id: 'refrigeratorMaintainedByLandlord',
    title: 'Refrigerator Maintained',
    type: 'checkbox',
  },
  {
    id: 'washerMaintainedByLandlord',
    title: 'Washer Maintained By Landlord',
    type: 'checkbox',
  },
  {
    id: 'hasOven',
    title: 'Oven',
    type: 'checkbox',
  },
  {
    id: 'ovenPhotoUrls',
    title: 'Oven Photo',
    type: 'file_multi',
    properties: {
      filePath: '/listing-uploads/appliance-photos/{uuid}',
      fileAccept: 'image/*',
    },
  },
  {
    id: 'hasGasStove',
    title: 'Gas Stove',
    type: 'select',
    properties: {
      transformToBoolean: true,
      enableResetOption: true,
      choices: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
    },
  },
  {
    id: 'gasStovePhotoUrls',
    title: 'Gas Stove Photo',
    type: 'file_multi',
    properties: {
      filePath: '/listing-uploads/appliance-photos/{uuid}',
      fileAccept: 'image/*',
    },
  },
  {
    id: 'hasElectricStove',
    title: 'Electric Stove',
    type: 'select',
    properties: {
      transformToBoolean: true,
      enableResetOption: true,
      choices: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
    },
  },
  {
    id: 'electricStovePhotoUrls',
    title: 'Electric Stove Photo',
    type: 'file_multi',
    properties: {
      filePath: '/listing-uploads/appliance-photos/{uuid}',
      fileAccept: 'image/*',
    },
  },
  {
    id: 'hasDishWasher',
    title: 'Dishwasher',
    type: 'select',
    properties: {
      transformToBoolean: true,
      enableResetOption: true,
      choices: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
    },
  },
  {
    id: 'dishWasherPhotoUrls',
    title: 'Dishwasher Photo',
    type: 'file_multi',
    properties: {
      filePath: '/listing-uploads/appliance-photos/{uuid}',
      fileAccept: 'image/*',
    },
  },
  {
    id: 'hasRefrigerator',
    title: 'Refrigerator',
    type: 'select',
    properties: {
      transformToBoolean: true,
      enableResetOption: true,
      choices: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
    },
  },
  {
    id: 'refrigeratorPhotoUrls',
    title: 'Refrigerator Photo',
    type: 'file_multi',
    properties: {
      filePath: '/listing-uploads/appliance-photos/{uuid}',
      fileAccept: 'image/*',
    },
  },
  {
    id: 'hasMicrowave',
    title: 'Microwave',
    type: 'select',
    properties: {
      transformToBoolean: true,
      enableResetOption: true,
      choices: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
    },
  },
  {
    id: 'microwavePhotoUrls',
    title: 'Microwave Photo',
    type: 'file_multi',
    properties: {
      filePath: '/listing-uploads/appliance-photos/{uuid}',
      fileAccept: 'image/*',
    },
  },
  {
    id: 'hasWasher',
    title: 'Washer In-Unit',
    type: 'select',
    properties: {
      transformToBoolean: true,
      enableResetOption: true,
      choices: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
    },
  },
  {
    id: 'washerPhotoUrls',
    title: 'Washer Photo',
    type: 'file_multi',
    properties: {
      filePath: '/listing-uploads/appliance-photos/{uuid}',
      fileAccept: 'image/*',
    },
  },
  {
    id: 'hasDryer',
    title: 'Dryer In-Unit',
    type: 'select',
    properties: {
      transformToBoolean: true,
      enableResetOption: true,
      choices: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
    },
  },
  {
    id: 'dryerPhotoUrls',
    title: 'Dryer Photo',
    type: 'file_multi',
    properties: {
      filePath: '/listing-uploads/appliance-photos/{uuid}',
      fileAccept: 'image/*',
    },
  },
  {
    id: 'hasWasherDryerConnections',
    title: 'Has Washer Dryer Connections',
    type: 'select',
    properties: {
      transformToBoolean: true,
      enableResetOption: true,
      choices: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
    },
  },
  {
    id: 'washerDryerConnectionsPhotoUrls',
    title: 'Washer Dryer Connections Photo',
    type: 'file_multi',
    properties: {
      filePath: '/listing-uploads/appliance-photos/{uuid}',
      fileAccept: 'image/*',
    },
  },
  {
    id: 'hasBambooFlooring',
    title: 'Bamboo',
    type: 'checkbox',
  },
  {
    id: 'hasBrick',
    title: 'Brick',
    type: 'checkbox',
  },
  {
    id: 'hasCarpetFlooring',
    title: 'Carpet',
    type: 'checkbox',
  },
  {
    id: 'hasConcreteFlooring',
    title: 'Concrete',
    type: 'checkbox',
  },
  {
    id: 'hasCorkFlooring',
    title: 'Cork',
    type: 'checkbox',
  },
  {
    id: 'hasLaminateFlooring',
    title: 'Laminate',
    type: 'checkbox',
  },
  {
    id: 'hasLinoleumFlooring',
    title: 'Linoleum',
    type: 'checkbox',
  },
  {
    id: 'hasMarbleFlooring',
    title: 'Marble',
    type: 'checkbox',
  },
  {
    id: 'hasNoCarpetFlooring',
    title: 'No Carpet',
    type: 'checkbox',
  },
  {
    id: 'hasParquetFlooring',
    title: 'Parquet',
    type: 'checkbox',
  },
  {
    id: 'hasSlateFlooring',
    title: 'Slate',
    type: 'checkbox',
  },
  {
    id: 'hasStoneFlooring',
    title: 'Stone',
    type: 'checkbox',
  },
  {
    id: 'hasTerrazzoFlooring',
    title: 'Terrazzo',
    type: 'checkbox',
  },
  {
    id: 'hasTileFlooring',
    title: 'Tile - Hard',
    type: 'checkbox',
  },
  {
    id: 'hasVinylTileFlooring',
    title: 'Tile - Vinyl',
    type: 'checkbox',
  },
  {
    id: 'hasVinylFlooring',
    title: 'Vinyl Plank',
    type: 'checkbox',
  },
  {
    id: 'hasVinylSheetFlooring',
    title: 'Vinyl Sheet',
    type: 'checkbox',
  },
  {
    id: 'hasWoodFlooring',
    title: 'Wood',
    type: 'checkbox',
  },
  {
    id: 'hasGarbageDisposal',
    title: 'Garbage Disposal',
    type: 'checkbox',
  },
  {
    id: 'petWeight',
    title: 'Pet Weight',
    type: 'select',
    properties: {
      choices: petWeightOptionItems,
    },
  },
  {
    id: 'dogsAllowed',
    title: 'Dogs Allowed',
    type: 'checkbox',
  },
  {
    id: 'catsAllowed',
    title: 'Cats Allowed',
    type: 'checkbox',
  },
  {
    id: 'petsAllowed',
    title: 'Pets Allowed',
    type: 'checkbox',
  },
  {
    id: 'petsNegotiable',
    title: 'Pets Negotiable',
    type: 'checkbox',
  },
  {
    id: 'maximumPetsAllowed',
    title: 'Max Pets',
    type: 'number',
  },
  {
    id: 'stories',
    title: 'Stories',
    type: 'number',
  },
  {
    id: 'hasFencedYards',
    title: 'Fenced Yards',
    type: 'checkbox',
  },
  {
    id: 'fenceDescription',
    title: 'Fence Description',
    type: 'select',
    properties: {
      // TODO(@d-oliveros): Move to constants file?
      choices: [
        {
          label: 'Chain Link',
          value: 'Chain Link',
        },
        {
          label: 'Electric',
          value: 'Electric',
        },
        {
          label: 'Fenced Private Yard',
          value: 'Fenced Private Yard',
        },
        {
          label: 'Game Fence',
          value: 'Game Fence',
        },
        {
          label: 'Goat Type',
          value: 'Goat Type',
        },
        {
          label: 'Invisible',
          value: 'Invisible',
        },
        {
          label: 'Livestock',
          value: 'Livestock',
        },
        {
          label: 'Masonry',
          value: 'Masonry',
        },
        {
          label: 'Mesh',
          value: 'Mesh',
        },
        {
          label: 'No Fence',
          value: 'No Fence',
        },
        {
          label: 'Non-Privacy',
          value: 'Non-Privacy',
        },
        {
          label: 'Partial',
          value: 'Partial',
        },
        {
          label: 'Picket',
          value: 'Picket',
        },
        {
          label: 'Pipe',
          value: 'Pipe',
        },
        {
          label: 'Post',
          value: 'Post',
        },
        {
          label: 'Privacy',
          value: 'Privacy',
        },
        {
          label: 'See Agent',
          value: 'See Agent',
        },
        {
          label: 'Split Rail',
          value: 'Split Rail',
        },
        {
          label: 'Vinyl',
          value: 'Vinyl',
        },
        {
          label: 'Wire - Barb',
          value: 'Wire - Barb',
        },
        {
          label: 'Wire - Smooth',
          value: 'Wire - Smooth',
        },
        {
          label: 'Wood',
          value: 'Wood',
        },
        {
          label: 'Wrought Iron',
          value: 'Wrought Iron',
        },
      ],
    },
  },
  {
    id: 'garageSpaces',
    title: 'Garage Spaces',
    type: 'number',
  },
  {
    id: 'parkingSpaces',
    title: 'Parking Spaces',
    type: 'number',
  },
  {
    id: 'yearBuilt',
    title: 'Year Built',
    type: 'number',
  },
  {
    id: 'legalDescription',
    title: 'Legal Description of the Property',
    type: 'long_text',
  },
  {
    id: 'yearRenovated',
    title: 'Year Renovated',
    type: 'number',
  },
  {
    id: 'renovatedDate',
    title: 'Renovated Date',
    type: 'date_picker',
  },
  {
    id: 'hasGarage',
    title: 'Garage',
    type: 'checkbox',
  },
  {
    id: 'hasFence',
    title: 'Fence',
    type: 'checkbox',
  },
  {
    id: 'hasYard',
    title: 'Yard',
    type: 'checkbox',
  },
  {
    id: 'hasCentralHeating',
    title: 'Central Heat',
    type: 'checkbox',
  },
  {
    id: 'hasNaturalGasHeating',
    title: 'Natural Gas Heat',
    type: 'checkbox',
  },
  {
    id: 'hasRadiatorHeating',
    title: 'Radiator Heat',
    type: 'checkbox',
  },
  {
    id: 'policyElectricPaid',
    title: 'Owner Pays Electric',
    type: 'checkbox',
  },
  {
    id: 'policyGasPaid',
    title: 'Owner Pays Gas',
    type: 'checkbox',
  },
  {
    id: 'policyTrashPaid',
    title: 'Owner Pays Trash',
    type: 'checkbox',
  },
  {
    id: 'policyWaterPaid',
    title: 'Owner Pays Water',
    type: 'checkbox',
  },
  {
    id: 'policyGardeningPaid',
    title: 'Owner Pays Lawn Care',
    type: 'checkbox',
  },
  {
    id: 'policyPestControlpaid',
    title: 'Owner Pays Pest Control',
    type: 'checkbox',
  },
  {
    id: 'hasCentralAc',
    title: 'Central Air',
    type: 'checkbox',
  },
  {
    id: 'applicationFee',
    title: 'Application Fee',
    type: 'currency',
  },
  {
    id: 'petDeposit',
    title: 'Pet Deposit',
    type: 'currency',
  },
  {
    id: 'petDepositPerPet',
    title: 'Pet Deposit Per Pet',
    type: 'checkbox',
  },
  {
    id: 'additionalPetDeposit',
    title: 'Aditional Pet Deposit',
    type: 'currency',
  },
  {
    id: 'securityDeposit',
    title: 'Security Deposit',
    type: 'currency',
  },
  {
    id: 'petFee',
    title: 'Pet Fee',
    type: 'currency',
  },
  {
    id: 'petFeePerPet',
    title: 'Pet Fee Per Pet',
    type: 'checkbox',
  },
  {
    id: 'additionalPetFee',
    title: 'Additional Pet Fee',
    type: 'currency',
  },
  {
    id: 'applicationPolicy',
    title: 'Application Policy',
    type: 'select',
    properties: {
      choices: [
        {
          label: 'First Qualified Application',
          value: LISTING_APPLICATION_POLICY_FIRST,
        },
        {
          label: 'Best Qualified Application',
          value: LISTING_APPLICATION_POLICY_BEST,
        },
      ],
    },
  },
  {
    id: 'administrationFee',
    title: 'Administration Fee',
    type: 'currency',
  },
  {
    id: 'residentBenefitsPackageFee',
    title: 'Resident Benefits Package Fee',
    type: 'currency',
  },
  {
    id: 'collectLastMonthRent',
    title: "Collect Last Month's Rent?",
    type: 'yes_no',
  },
  {
    id: 'collectRbpFee',
    title: 'Collect Resident Benefits Package?',
    type: 'yes_no',
  },
  {
    id: 'minLeaseMonths',
    title: 'Min. Lease Months',
    type: 'number',
  },
  {
    id: 'acceptsGuarantor',
    title: 'Lease Guarantor?',
    type: 'checkbox',
  },
  {
    id: 'monthlyPetRent',
    title: 'Monthly Pet Rent',
    type: 'currency',
  },
  {
    id: 'monthlyPetRentPerPet',
    title: 'Monthly Pet Rent Per Pet',
    type: 'checkbox',
  },
  {
    id: 'additionalMonthlyPetRent',
    title: 'Aditional Monthly Pet Rent',
    type: 'currency',
  },
  {
    id: 'maxLeaseMonths',
    title: 'Max. Lease Months',
    type: 'number',
  },
  {
    id: 'leaseLengthNegotiable',
    title: 'Lease Length Negotiable?',
    type: 'checkbox',
  },
  {
    id: 'section8',
    title: 'Section 8',
    type: 'checkbox',
  },
  {
    id: 'accessMethod',
    title: 'Property Access Method',
    type: 'multiple_choice',
    properties: {
      multipleSelection: false,
      useArrayValues: false,
      choices: PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS,
      fieldType: SR_CHECKBOX_THEME_BOX,
      autoFitWidth: '400px',
    },
  },
  {
    id: 'propertyAccessCode',
    title: 'Access Code',
    type: 'short_text',
  },
  {
    id: 'supraCbsCode',
    title: 'Supra CBS Code',
    type: 'short_text',
    properties: {
      placeholder: '1234567',
      pattern: '[0-9]*',
    },
    validations: ['supraCbs'],
  },
  {
    id: 'dateKeyExpectedAtListing',
    title: 'Date the key will be at the listing',
    subtitle:
      'If the key is not at the listing on the date indicated, the listing setup may be delayed.',
    type: 'date_picker',
  },
  {
    id: 'propertySetupAccessType',
    title: 'Key Access',
    type: 'multiple_choice',
    properties: {
      multipleSelection: false,
      useArrayValues: false,
      choices: PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS,
      fieldType: SR_CHECKBOX_THEME_BOX,
      autoFitWidth: '400px',
    },
  },
  {
    id: 'keyPickupDescription',
    title: '',
    type: 'long_text',
  },
  {
    id: 'propertyAccessContactName',
    title: 'Key contact name',
    type: 'short_text',
  },
  {
    id: 'propertyAccessContactPhone',
    title: 'Key contact phone',
    type: 'short_text',
  },
  {
    id: 'propertyAccessContactEmail',
    title: 'Key contact email',
    type: 'short_text',
  },
  {
    id: 'gateCode',
    title: 'Security (gate code)',
    type: 'short_text',
  },
  {
    id: 'alarmCode',
    title: 'Alarm Code',
    label: '(optional)',
    type: 'short_text',
    properties: {
      placeholder: '*4321',
    },
  },
  {
    id: 'showingInstructions',
    title: 'Special Showing Instructions',
    label: '(optional)',
    type: 'long_text',
    properties: {
      placeholder: 'Write instructions here…',
    },
  },
  {
    id: 'lockboxCode',
    title: 'Combo Lockbox Code',
    label: '(optional)',
    type: 'short_text',
    properties: {
      placeholder: '6789',
    },
  },
  {
    id: 'elevatorInBuilding',
    title: 'Elevator in Building',
    type: 'checkbox',
  },
  {
    id: 'hasBasement',
    title: 'Basement',
    type: 'checkbox',
  },
  {
    id: 'hasPatio',
    title: 'Patio',
    type: 'checkbox',
  },
  {
    id: 'hasBalcony',
    title: 'Balcony',
    type: 'checkbox',
  },
  {
    id: 'hasDeck',
    title: 'Deck',
    type: 'checkbox',
  },
  {
    id: 'isFurnished',
    title: 'Furnished',
    type: 'checkbox',
  },
  {
    id: 'isNotFurnished',
    title: 'Unfurnished',
    type: 'checkbox',
  },
  {
    id: 'smokeFree',
    title: 'Smoke Free',
    type: 'checkbox',
  },
  {
    id: 'hasCommunalWasherDryer',
    title: 'Communal Washer & Dryer',
    type: 'checkbox',
  },
  {
    id: 'monthToMonthLeaseTerm',
    title: 'Month to Month',
    type: 'checkbox',
  },
  {
    id: 'rentToOwnLeaseTerm',
    title: 'Rent-to-Own',
    type: 'checkbox',
  },
  {
    id: 'highSpeedInternetIncluded',
    title: 'High-Speed Internet Included',
    type: 'checkbox',
  },
  {
    id: 'highSpeedInternetAvailable',
    title: 'High-Speed Internet Available',
    type: 'checkbox',
  },
  {
    id: 'hasRampedEntry',
    title: 'No-Step or Ramped Entry',
    type: 'checkbox',
  },
  {
    id: 'hasAccessibleParking',
    title: 'Accessible Parking',
    type: 'checkbox',
  },
  {
    id: 'hasLevelStyleDoorHandles',
    title: 'Lever Style Door Handles',
    type: 'checkbox',
  },
  {
    id: 'environmentalControlsAboveFloor',
    title: 'Environmental Controls 15"-48" Above Floor',
    type: 'checkbox',
  },
  {
    id: 'grabBarsInBathroom',
    title: 'Grab Bars in Bathroom',
    type: 'checkbox',
  },
  {
    id: 'lowTresholdShower',
    title: 'Low Threshold or Roll-In shower',
    type: 'checkbox',
  },
  {
    id: 'hasTTurnInBathroom',
    title: "5' Turning Radius or T-Turn in Bathroom",
    type: 'checkbox',
  },
  {
    id: 'accessibleBathroomVanities',
    title: 'Accessible Bathroom Vanities',
    type: 'checkbox',
  },
  {
    id: 'hasFrontLoadingKitchenAppliances',
    title: 'Front Loading or Front Control Kitchen Appliances',
    type: 'checkbox',
  },
  {
    id: 'hasAccessibleKitchenCounters',
    title: 'Accessible Kitchen Counters',
    type: 'checkbox',
  },
  {
    id: 'hasClearFloorAreaFrontOfSink',
    title: 'Clear Floor Area in Front of Sink and Range',
    type: 'checkbox',
  },
  {
    id: 'has32WideDoorways',
    title: '32" or Wider Doorways',
    type: 'checkbox',
  },
  {
    id: 'has36WideDoorways',
    title: '36" or Wider Hallways',
    type: 'checkbox',
  },
  {
    id: 'hasAirConditioning',
    title: 'Air Conditioning',
    type: 'checkbox',
  },
  {
    id: 'largeDogsAllowed',
    title: 'Large Dogs Allowed',
    type: 'checkbox',
  },
  {
    id: 'electricityIncluded',
    title: 'Electricity Included',
    type: 'checkbox',
  },
  {
    id: 'gasIncluded',
    title: 'Gas Included',
    type: 'checkbox',
  },
  {
    id: 'heatIncluded',
    title: 'Heat Included',
    type: 'checkbox',
  },
  {
    id: 'waterIncluded',
    title: 'Water Included',
    type: 'checkbox',
  },
  {
    id: 'trashPickUpIncluded',
    title: 'Trash Pick-up Included',
    type: 'checkbox',
  },
  {
    id: 'hasWaterSoftener',
    title: 'Water Softener',
    type: 'checkbox',
  },
  {
    id: 'description',
    title: 'Marketing Description',
    type: 'long_text',
  },
  {
    id: 'showingRemarks',
    title: 'Private Remarks',
    type: 'long_text',
  },
  {
    id: 'hasOnlinePayments',
    title: 'Online Payments',
    type: 'checkbox',
  },
  {
    id: 'valetTrashFee',
    title: 'Valet Trash Fee',
    type: 'currency',
  },
  {
    id: 'pestControlFee',
    title: 'Pest Control Fee',
    type: 'currency',
  },
  {
    id: 'wasteWaterFee',
    title: 'Waste Water Fee',
    type: 'currency',
  },
  {
    id: 'monthlyCommunityFee',
    title: 'Monthly Community Fee',
    type: 'currency',
  },
  {
    id: 'monthlyCommunityFeeDescription',
    title: 'Monthly Community Fee Description',
    type: 'long_text',
  },
  {
    id: 'carportFee',
    title: 'Carport Fee',
    type: 'currency',
  },
  {
    id: 'reservedParkingSpotFee',
    title: 'Reserved Parking Spot Fee',
    type: 'currency',
  },
  {
    id: 'bikeRackFee',
    title: 'Bike Rack Fee',
    type: 'currency',
  },
  {
    id: 'electricFee',
    title: 'Electric Fee',
    type: 'currency',
  },
  {
    id: 'gasFee',
    title: 'Gas Fee',
    type: 'currency',
  },
  {
    id: 'waterAndSewerFee',
    title: 'Water And Sewer Fee',
    type: 'currency',
  },
  {
    id: 'cableAndInternetCombinedFee',
    title: 'Cable And Internet Combined Fee',
    type: 'currency',
  },
  {
    id: 'cableFee',
    title: 'Cable Fee',
    type: 'currency',
  },
  {
    id: 'internetFee',
    title: 'Internet Fee',
    type: 'currency',
  },
  {
    id: 'packageLockerSystemFee',
    title: 'Package Locker System Fee',
    type: 'currency',
  },
  {
    id: 'coveredParkingFee',
    title: 'Covered Parking Fee',
    type: 'currency',
  },
  {
    id: 'storageFee',
    title: 'Storage Fee',
    type: 'currency',
  },
  {
    id: 'surfaceParkingFee',
    title: 'Surface Parking Fee',
    type: 'currency',
  },
  {
    id: 'garageAttachedParkingSpotFee',
    title: 'Garage Attached Parking Spot Fee',
    type: 'currency',
  },
  {
    id: 'garageDetachedParkingSpotFee',
    title: 'Garage Detached Parking Spot Fee',
    type: 'currency',
  },
  {
    id: 'hasWheelchairAccessible',
    title: 'Wheelchair accessible',
    type: 'checkbox',
  },
  {
    id: 'coveredParkingCount',
    title: 'Covered Parking count',
    type: 'number',
  },
  {
    id: 'surfaceParkingCount',
    title: 'Surface Parking count',
    type: 'number',
  },
  {
    id: 'garageAttachedParkingSpots',
    title: 'Garage (Attached) Parking Spots',
    type: 'number',
  },
  {
    id: 'garageDetachedParkingSpots',
    title: 'Garage (Detached) Parking Spots',
    type: 'number',
  },
  {
    id: 'hasOnSiteManagement',
    title: 'On-site Management',
    type: 'checkbox',
  },
  {
    id: 'hasControlledBuildingAccess',
    title: 'Controlled Building Access',
    type: 'checkbox',
  },
  {
    id: 'hasCeilingFans',
    title: 'Ceiling Fan(s)',
    type: 'checkbox',
  },
  {
    id: 'hasFireplace',
    title: 'Fireplace',
    type: 'checkbox',
  },
  {
    id: 'hasExtraStorage',
    title: 'Outside /Extra Storage',
    type: 'checkbox',
  },
  {
    id: 'hasWalkInClosets',
    title: 'Walk-in Closet(s)',
    type: 'checkbox',
  },
  {
    id: 'kitchenAppliancesColor',
    title: 'Appliances Color',
    type: 'select',
    properties: {
      choices: [
        { label: 'Black', value: 'Black' },
        { label: 'White', value: 'White' },
        { label: 'Stainless/SS Look', value: 'Stainless/SS Look' },
      ],
    },
  },
  {
    id: 'kitchenCountertopMaterial',
    title: 'Kitchen Countertop Material',
    type: 'select',
    properties: {
      choices: [{ label: 'Granite', value: 'Granite' }],
    },
  },
  {
    id: 'hasShowerTub',
    title: 'Shower Tub',
    type: 'checkbox',
  },
  {
    id: 'hasDogWalkPark',
    title: 'Dog Park',
    type: 'checkbox',
  },
  {
    id: 'hasBusinessCenter',
    title: 'Business Center',
    type: 'checkbox',
  },
  {
    id: 'hasConferenceRoom',
    title: 'Conference Room',
    type: 'checkbox',
  },
  {
    id: 'hasJoggingTrail',
    title: 'Jogging/Walking Trails',
    type: 'checkbox',
  },
  {
    id: 'hasRoofDeck',
    title: 'Roof Deck',
    type: 'checkbox',
  },
  {
    id: 'hasStackedWasherDryer',
    title: 'Stacked Washer Dryer',
    type: 'checkbox',
  },
  {
    id: 'electronicLockCode',
    title: 'Electronic Lock Code',
    type: 'short_text',
  },
  {
    id: 'electronicLockInstructions',
    title: 'Electronic Lock Instructions',
    type: 'long_text',
  },
  {
    id: 'externalApplicationLink',
    title: 'External Application Link',
    type: 'short_text',
  },
  {
    id: 'apartmentEmail',
    title: 'Apartment Email',
    type: 'email',
    validations: ['email'],
  },
  {
    id: 'apartmentPhone',
    title: 'Apartment Phone',
    type: 'phone',
    validations: ['phone'],
  },
  {
    id: 'leaseTemplateType',
    title: 'Lease Template',
    type: 'multiple_choice',
    properties: {
      choices: [
        {
          label: 'TAR standard',
          value: LEASE_TEMPLATE_TYPE_STANDARD,
          iconUrl: '/images/lease-tar-grey.svg',
          activeIconUrl: '/images/lease-tar-green.svg',
        },
        {
          label: 'Custom Lease',
          value: LEASE_TEMPLATE_TYPE_CUSTOM_LEASE,
          iconUrl: '/images/lease-custom-grey.svg',
          activeIconUrl: '/images/lease-custom-green.svg',
        },
      ],
      multipleSelection: false,
      fieldType: SR_CHECKBOX_LIST_THEME_ICON_BOX,
    },
  },
  {
    id: 'customLeaseTemplateUrl',
    title: 'Upload Custom Lease',
    type: 'file_single',
    properties: {
      fileAccept: '*',
      filePath: '/listing-uploads/lease-templates/{uuid}',
      placeholder: 'Tap to Upload Custom Lease',
      successMessage: 'Custom Lease Saved!',
      downloadButtonLabel: 'Download Lease',
    },
  },
  {
    id: 'rentPaymentRecipientName',
    title: 'Name for Payment',
    type: 'short_text',
  },
  {
    id: 'rentPaymentRecipientAddress',
    title: 'Address for Renter to Send Payment',
    type: 'short_text',
  },
  {
    id: 'rentPaymentMethods',
    title: 'Acceptable Types of Payment',
    label: ' (select 1 or more)',
    type: 'multiple_choice',
    properties: {
      choices: rentPaymentOptionItems,
    },
  },
  {
    id: 'rentPaymentOtherMethodDescription',
    title: 'Extra Description',
    type: 'long_text',
  },
  {
    id: 'isOwnerManaged',
    title: 'Owner Managed',
    type: 'yes_no',
  },
  {
    id: 'propertyManagerType',
    title: 'Type of Property Manager',
    type: 'multiple_choice',
    properties: {
      choices: propertyManagerOptionItems,
      multipleSelection: false,
      fieldType: SR_CHECKBOX_THEME_BOX,
    },
  },
  {
    id: 'propertyManagerName',
    title: 'Name',
    type: 'short_text',
  },
  {
    id: 'propertyManagerPhone',
    title: 'Phone',
    type: 'phone',
    validations: ['phone'],
  },
  {
    id: 'propertyManagerEmail',
    title: 'Email',
    type: 'email',
    validations: ['email'],
  },
  {
    id: 'invoiceRecipientName',
    title: 'Name',
    type: 'short_text',
  },
  {
    id: 'invoiceRecipientEmail',
    title: 'Phone',
    type: 'phone',
    validations: ['phone'],
  },
  {
    id: 'invoiceRecipientPhone',
    title: 'Email',
    type: 'email',
    validations: ['email'],
  },
  {
    id: 'availableDate',
    title: 'Available Date',
    type: 'date_picker',
  },
  {
    id: 'occupantType',
    title: 'Occupant Type',
    type: 'select',
    properties: {
      choices: LISTING_OCCUPANT_TYPES.map((listingType) => ({
        label: listingType,
        value: listingType,
      })),
    },
    label: '(This is posted to the MLS for buyer agents to schedule showings.)',
  },
  {
    id: 'occupantName',
    title: 'Occupant Name',
    type: 'short_text',
    properties: {
      placeholder: 'Jane Smith',
    },
  },
  {
    id: 'occupantPhone',
    title: 'Occupant Phone',
    type: 'short_text',
    properties: {
      placeholder: '5125551234',
    },
  },
  {
    id: 'occupantEmail',
    title: 'Occupant Email',
    type: 'short_text',
    properties: {
      placeholder: 'jsmith@gmail.com',
    },
  },
  {
    id: 'vacantDate',
    title: 'Date property will be vacant?',
    type: 'date_picker',
    properties: {
      placeholderValue: 'MM/DD/YYYY',
      placeholder: 'MM/DD/YYYY',
      dateFormat: 'MM/DD/YYYY',
      readOnly: false,
    },
  },
  {
    id: 'requirePhotos',
    title: 'Photos needed?',
    type: 'yes_no',
  },
  {
    id: 'linkToExistingPhotos',
    title: 'Link to the photos',
    label: '(dropbox, zillow, etc.)',
    type: 'short_text',
    properties: {
      placeholder: 'Paste the link to your photos...',
    },
  },
  {
    id: 'availableForPhotoDate',
    title: 'Earliest day Sunroom can take photos?',
    type: 'date_picker',
    properties: {
      placeholderValue: 'MM/DD/YYYY',
      placeholder: 'MM/DD/YYYY',
      dateFormat: 'MM/DD/YYYY',
      readOnly: false,
    },
  },
  {
    id: 'availableForPhotoDateSchedule',
    title: 'Can Sunroom go take photos any time during the day?',
    type: 'multiple_choice',
    properties: {
      multipleSelection: false,
      useArrayValues: false,
      choices: AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_OPTION_ITEMS,
      fieldType: SR_CHECKBOX_THEME_BOX,
      autoFitWidth: '250px',
    },
  },
  {
    id: 'availableForPhotoContactName',
    title: 'Scheduling contact name',
    type: 'short_text',
  },
  {
    id: 'availableForPhotoContactPhone',
    title: 'Scheduling contact phone',
    type: 'short_text',
  },
  {
    id: 'coListingAgentMlsNumber',
    title: '',
    type: 'short_text',
  },
  {
    id: 'postToMls',
    title: 'Post To MLS',
    type: 'select',
    properties: {
      choices: postToMlsOptionItems,
    },
  },
  {
    id: 'isSyndicated',
    title: 'Syndicate?',
    type: 'checkbox',
  },
  {
    id: 'buyersAgentCommission',
    title: 'Buyer’s Agent Commission',
    type: 'percentage',
    properties: {
      pattern: '[0-9]*',
      placeholder: '100',
      onlyAcceptNumbers: true,
    },
  },
  {
    id: 'comparativeMarketAnalysisRentPrice',
    title: 'CMA Rent Price',
    type: 'currency',
  },
  {
    id: 'comparativeMarketAnalysisNotes',
    title: 'Notes for Partner',
    type: 'long_text',
  },
  {
    id: 'comparativeMarketAnalysisPdfUrl',
    title: 'CMA Document Upload',
    type: 'file_single',
  },
  {
    id: 'brokerPriceOpinionPdfUrl',
    title: 'BPO Document Upload',
    type: 'file_single',
  },
  {
    id: 'photoUrls',
    title: 'Upload Photos',
    type: 'file_multi',
  },
  {
    id: 'propertyOwnerName',
    title: 'Property Owner Name',
    type: 'short_text',
  },
  {
    id: 'propertyOwnerPhone',
    title: 'Property Owner Phone',
    type: 'phone',
    validations: ['phone'],
  },
  {
    id: 'propertyOwnerEmail',
    title: 'Property Owner Email',
    type: 'email',
    validations: ['email'],
  },
  {
    id: 'propertySecondOwnerName',
    title: 'Property Second Owner Name',
    type: 'short_text',
  },
  {
    id: 'propertySecondOwnerPhone',
    title: 'Property Second Owner Phone',
    type: 'phone',
    validations: ['phone'],
  },
  {
    id: 'propertySecondOwnerEmail',
    title: 'Property Second Owner Email',
    type: 'email',
    validations: ['email'],
  },
  {
    id: 'marketingInfoCollectedFromOwner',
    title: 'Extra marketing info collected:',
    type: 'long_text',
  },
];

export const additionalDetailsFields = [
  {
    id: 'hasRefrigerator',
    title: 'Does it have a Refrigerator?',
    type: 'yes_no',
    validations: ['required'],
  },
  {
    id: 'hasWasher',
    title: 'Does it have a Washer In-Unit?',
    type: 'yes_no',
    validations: ['required'],
  },
  {
    id: 'hasDryer',
    title: 'Does it have a Dryer In-Unit?',
    type: 'yes_no',
    validations: ['required'],
  },
  {
    id: 'hasWasherDryerConnections',
    title: 'Does it have Washer/Dryer Connections?',
    type: 'yes_no',
    validations: ['required'],
  },
  {
    id: 'hasMicrowave',
    title: 'Does it have a Microwave?',
    type: 'yes_no',
    validations: ['required'],
  },
  {
    id: 'hasGasStove',
    title: 'Does it have a Gas Stove?',
    type: 'yes_no',
    validations: ['required'],
  },
  {
    id: 'hasElectricStove',
    title: 'Does it have an Electric Stove?',
    type: 'yes_no',
    validations: ['required'],
  },
  {
    id: 'hasDishWasher',
    title: 'Does it have a Dishwasher?',
    type: 'yes_no',
    validations: ['required'],
  },
];

const listingFormFieldsById = lodashKeyBy('id', listingFormFields);

export default function getListingFormFields(params) {
  typeCheck('params::NonEmptyObject', params);
  typeCheck('formType::Maybe NonEmptyString', params.formType);
  const fieldgroup = listingFormTypeToFieldgroupMapping[params.formType] || params.fieldgroup;
  const updates = params.updates || {};
  const isRequired = params.required || false;
  typeCheck('fieldgroup::Array', fieldgroup);
  typeCheck('updates::Object', updates);

  return fieldgroup
    .filter((listingFieldId) => listingFormFieldsById[listingFieldId])
    .map((listingFieldId) => {
      const listingFormField = lodashDefaultsDeep(
        {
          ...listingFormFieldsById[listingFieldId],
          validations: listingFormFieldsById[listingFieldId].validations || [],
          properties: listingFormFieldsById[listingFieldId].properties || {},
        },
        updates[listingFieldId],
      );

      if (isRequired && !listingFormField.validations.includes('required')) {
        listingFormField.validations.unshift('required');
      }

      return hideFieldIf(listingFormField.hideFieldIf, listingFormField);
    });
}
